import axios from 'axios'
import { Request } from '@/domain/Request'
import FileData from '@/domain/FileData'

/**
 * 検索条件
 */
export class SerchConditon {
    /** 受付番号 */
    requestNo = ""
    /** 期間開始日 */
    startOn = ""
    /** 期間終了日 */
    endOn = ""
    /** 顧客都道府県コード */
    customerPrefectureCode = ""
    /** 顧客コード */
    customerCode = ""
    /** 顧客枝番 */
    customerBranchNumber = ""
    /** 顧客名 */
    customerName = ""
    /** 検査依頼種別 */
    requestType: number | null = null
    /** 備考 */
    note = ""
    /* 有効 */
    isActive = true

    /* 画面用 */
    /** 期間タイプ */
    dateType = 3

    /**
     * コンストラクタ
     * @param init 
     */
    constructor(init?: Partial<SerchConditon>) {
        Object.assign(this, init)
    }
}

/**
 * 検体着日情報
 */
export class ReceiptInfo {
    /** 検体着日 */
    receiptOn = ""
    /** 検体受取ユーザID */
    receiptUserId = ""
    /** 検体受取ユーザー */
    receiptUserName = ""
    /** 重量 */
    weight: number | null = null

    /**
     * コンストラクタ
     * @param init 
     */
    constructor(init?: Partial<ReceiptInfo>) {
        Object.assign(this, init)
    }
}

/**
 * 検査依頼サービス
 */
export default class RequestsService {
    /** インスタンス */
    private static _instance: RequestsService

    /**
     * コンストラクタ
     */
    // eslint-disable-next-line
    private constructor() {}

    /**
     * インスタンスの取得
     */
    public static get instance() {
        if (!this._instance) {
            this._instance = new RequestsService();
        }
        return this._instance;
    }

    /**
     * 新規作成
     */
    public async createNew(): Promise<Request> {
        return await axios.get("/api/request/new")
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            // eslint-disable-next-line
            .catch((error) => {
                throw error
            })
    }

    /**
     * 参照新規作成
     */
    public async referenceNew(estimateNo: string): Promise<Request> {
        return await axios.get("/api/request/new", {params: {estimateNo: estimateNo}})
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            // eslint-disable-next-line
            .catch((error) => {
                throw error
            })
    }

    /**
     * 複写作成
     */
    public async copyNew(requestNo: string): Promise<Request> {
        return await axios.get(`/api/request/copy_new?requestNo=${requestNo}`)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            // eslint-disable-next-line
            .catch((error) => {
                throw error
            })
    }

    /**
     * 編集
     */
    public async edit(no: string): Promise<Request> {
        return await axios.get(`/api/request/edit/${no}`)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 新規保存
     */
    public async create(dto: Request): Promise<Request> {
        if (dto.requestType !== 2) {
            dto.requestId = ""
        }
        if (dto.weight === null) {
            dto.weight = 0
        }
        return await axios.post("/api/request/new", dto)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 保存
     */
    public async update(dto: Request): Promise<Request> {
        if (dto.requestType !== 2) {
            dto.requestId = ""
        }
        if (dto.weight === null) {
            dto.weight = 0
        }
        return await axios.put(`/api/request/edit/${dto.requestNo}`, dto)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 削除
     */
    public async delete(no: string): Promise<Request> {
        return await axios.delete(`/api/request/delete/${no}`)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 検体受け取り
     */
    public async updateReceiptOn(requestNo: string, receiptInfo: ReceiptInfo): Promise<Request> {
        if (receiptInfo.weight === null) {
            receiptInfo.weight = 0
        }
        return await axios.put(`/api/request/receiptOn/${requestNo}`, receiptInfo)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 検査依頼一覧を検索
     */
    public async findAll(serchConditon: SerchConditon): Promise<Request[]> {
        return await axios.get("/api/requests", {params: serchConditon})
            .then((results) => {
                if (results.status === 200) {
                    return (results.data as Request[]).map(x => this.convert(x))
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 依頼書ダウンロード
     */
    public async download(requestNo: string): Promise<FileData> {
        return await axios.get(`/api/request/download/${requestNo}`)
            .then((results) => {
                if (results.status === 200) {
                    return new FileData({ ...(results.data)})
                } else {
                    throw results.statusText
                }
            })
            // eslint-disable-next-line
            .catch((error) => {
                throw error
            })
    }

    /**
     * 受付番号存在チェック
     */
    public async has(requestNo: string): Promise<boolean> {
        return await axios.get(`/api/request/has/${requestNo}`)
            .then((results) => {
                if (results.status === 200) {
                    return results.data
                } else {
                    throw results.statusText
                }
            })
            // eslint-disable-next-line
            .catch((error) => {
                throw error
            })
    }

    /**
     * 受信データの変換
     * @param data
     */
    // eslint-disable-next-line
    private convert(data: any): Request {
        const request = data as Request
        request.arriveOn = request.arriveOn || ""
        request.receiptOn = request.receiptOn || ""
        request.deliveryOn = request.deliveryOn || ""
        request.receiptUser.userId = request.receiptUser.userId || ""
        request.receiptUser.userName = request.receiptUser.userName || ""
        request.inspectionOn = request.inspectionOn || ""
        request.inspectionUser.userId = request.inspectionUser.userId || ""
        request.inspectionUser.userName = request.inspectionUser.userName || ""
        request.weight = request.weight === 0 ? null : request.weight
        request.reportOn = request.reportOn || ""

        return new Request({ ...request })
    }
}
